import {
  isDateObject,
  castToBoolean,
  createError,
  formattedDurationInHours,
  normalizeSingleDayStartEndDates,
} from '../../../../../lib/util';
import Claim from '../../../../../lib/model/Claim';

const defaultStartTime = new Date('Thu, 01 Jan 1970 12:00:00');

class SecondaryClaimEditorController {
  constructor(
    $q,
    $scope,
    LogService,
    ClaimCategoryService,
    ClaimService,
    DateTimeService,
    DocumentService
  ) {
    "ngInject";
    this.$q = $q;
    this.$scope = $scope;
    this.LogService = LogService;
    this.ClaimService = ClaimService;
    this.ClaimCategoryService = ClaimCategoryService;
    this.DateTimeService = DateTimeService;
    this.DocumentService = DocumentService;
    this._claimCategories = [];
    this._claimTypesByCategoryId = {};
    this._errors = [];
    this._constructed = true;
    this._loading = false;
    this._receiptFileForUpload = null;
    this._distanceLimitExceptionAcknowledged = false;
    this._distanceLimitException = false;
    this._timeLimitExceptionAcknowledged = false;
    this._timeLimitException = false;

    /**
     * Date Pickers
     */
    this._datePickersOpened = {};
    this._datePickersOptions = {};

    const datePickerNames = ['transaction_date'];
    const defaultDatePickerOptions = {
      minDate: null,
      maxDate: null,
    };
    for (let datePickerName of datePickerNames) {
      this._datePickersOpened[datePickerName] = false;
      this._datePickersOptions[datePickerName] = {...defaultDatePickerOptions};
    }


    this._claimStartTime = null;
    this._claimEndTime = null;

    this.$scope.$on('clearSecondaryShiftSegments', () =>{
      if (this.editingClaim && this.editingClaim.routeSegment) {
        this.editingClaim.routeSegment = null;
        this.editingClaim.route_segments_id = null;
      }
    });
  }

  $onInit() {
    const promises = [];

    // Register handler for auto saving secondary claim on parent controller
    this._addAutoSaveHandler();

    promises.push(
      this.ClaimCategoryService.getAll()
        .then(claimCategories => {
          this._claimCategories = claimCategories;
        })
    );

    this._loading = true;
    return this.$q.all(promises)
      .then(() => {
        this.reset();
      })
      .finally(() => {
        this._loading = false;
      });
  }

  $onDestroy() {
    this._removeAutoSaveHandler();
  }

  get loading() {
    return this._loading;
  }

  set position(position) {
    this._position = position;
  }

  get position() {
    return this._position;
  }

  set staffUser(staffUser) {
    this._staffUser = staffUser;
  }

  get staffUser() {
    return this._staffUser;
  }

  set claim(claim) {
    this._claim = claim;
    if (this._constructed) {
      this.reset();
    }

    if (claim && claim.route_segments_id) {
      if (this.staffUser && this.staffUser.route_segments) {
        let segment = this.staffUser.route_segments.filter(segment => String(segment.id) === String(claim.route_segments_id));
        claim.routeSegment = segment.pop();
      }
    }
  }

  get claim() {
    return this._claim;
  }

  get isNewClaim() {
    return !this.claim;
  }

  get isPristine() {
    const form = this.$scope.secondaryClaimForm;
    return form && form.$pristine;
  }

  get isEmptyNewClaim() {
    return this.isNewClaim && this.isPristine;
  }

  get isSyncedToServer() {
    return this.claim && this.claim.isSynced;
  }

  set editingClaim(claim) {
    this._editingClaim = claim;
  }

  get editingClaim() {
    return this._editingClaim;
  }

  get claimCategories() {
    return this._claimCategories;
  }

  get claimTypes() {
    const claimCategoryId = this.editingClaim && this.editingClaim.category && this.editingClaim.category.id || 0;
    if (this._claimTypesByCategoryId[claimCategoryId] === undefined) {
      if (claimCategoryId) {
        this._claimTypesByCategoryId[claimCategoryId] = this.event.claimTypesForCategoryId(claimCategoryId);
      } else {
        this._claimTypesByCategoryId[claimCategoryId] = [];
      }
    }
    let claimTypes = this._claimTypesByCategoryId[claimCategoryId];

    // Filter out any claim types that are not allowed for this position (based upon position_access).
    if (claimTypes && this.position.position_id) {
      claimTypes = claimTypes.filter(claimType => claimType.position_access.includes(parseInt(this.position.position_id)));
    }

    /**
     * Filter out claim types that are already used for the primary claim or any other secondary claims
     */

    if (this.event.used_claim_type_ids) {
      claimTypes = claimTypes.filter(
        claimType =>
          (claimType.category && claimType.category.constant === 'CLAIM_CATEGORY_AMOUNT')
          || this.event.used_claim_type_ids.indexOf(parseInt(claimType.id)) === -1
          || (this.editingClaim && this.editingClaim.type && parseInt(claimType.id) === parseInt(this.editingClaim.type.id))
          || (castToBoolean(claimType.collect_route_segments) && (!castToBoolean(this.event.opt_out_travel_standard) || !castToBoolean(this.event.opt_out_travel_tiered)))
      );
    }

    /**
     * Filter out auto created claim types UNLESS the claim is already using it
     */
    claimTypes = claimTypes.filter(claimType => (!castToBoolean(claimType.auto_create)) || (this.editingClaim.type && parseInt(this.editingClaim.type.id) === parseInt(claimType.id)));

    return claimTypes;
  }

  set errors(errors) {
    this._errors = errors;
  }

  get errors() {
    return this._errors;
  }

  set canEdit(canEdit) {
    this._canEdit = canEdit;
  }

  get canEdit() {
    return this._canEdit;
  }

  get showAdd() {
    return this.isNewClaim;
  }

  get showSave() {
    return !this.isNewClaim;
  }

  get showRemove() {
    return !this.isNewClaim && !this.resubmitting && this.editingClaim && !this.editingClaim.isApproved;
  }

  get showReset() {
    return this.isNewClaim;
  }

  get isAmountBased() {
    return this.editingClaim && this.editingClaim.category && this.editingClaim.category.isAmountBased;
  }

  get isTimeBased() {
    return this.editingClaim && this.editingClaim.category && this.editingClaim.category.isTimeBased;
  }

  get isDistanceBased() {
    return this.editingClaim && this.editingClaim.category && this.editingClaim.category.isDistanceBased;
  }

  get allowReceipt() {
    return this.editingClaim && this.editingClaim.type && castToBoolean(this.editingClaim.type.secondary_claims_allow_receipts);
  }

  get distanceLimitExceptionAcknowledged() {
    return this._distanceLimitExceptionAcknowledged;
  }

  set distanceLimitExceptionAcknowledged(val) {
    this._distanceLimitExceptionAcknowledged = val;
  }

  get distanceLimitException() {
    return this._distanceLimitException;
  }

  set distanceLimitException(val) {
    this._distanceLimitException = val;
  }

  get timeLimitExceptionAcknowledged() {
    return this._timeLimitExceptionAcknowledged;
  }

  set timeLimitExceptionAcknowledged(val) {
    this._timeLimitExceptionAcknowledged = val;
  }

  get timeLimitException() {
    return this._timeLimitException;
  }

  set timeLimitException(val) {
    this._timeLimitException = val;
  }

  set receiptFileForUpload(file) {
    this._receiptFileForUpload = file;
  }

  get receiptFileForUpload() {
    return this._receiptFileForUpload;
  }

  get receiptDocument() {
    return this.editingClaim && this.editingClaim.receiptDocument;
  }

  set claimStartTime(time) {
    this._claimStartTime = time;
  }

  get claimStartTime() {
    return this._claimStartTime;
  }

  set claimEndTime(time) {
    this._claimEndTime = time;
  }

  get claimStartTimeShifted() {
    const claimStartTime = this.claimStartTime;
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!claimStartTime || !eventTimezone) {
      return claimStartTime;
    }
    const shifted = this.DateTimeService.shiftDateObjectFromLocal(claimStartTime, eventTimezone);

    if (
      this._lastClaimStartTimeShifted
      && this._lastClaimStartTimeShifted.getTime() === shifted.getTime()
    ) {
      return this._lastClaimStartTimeShifted;
    }

    this._lastClaimStartTimeShifted = shifted;
    return shifted;
  }

  set claimStartTimeShifted(date) {
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!date) {
      this.claimStartTime = date;
      return;
    }

    this.claimStartTime = this.DateTimeService.unshiftDateObjectFromLocal(date, eventTimezone);
  }

  get claimEndTimeShifted() {
    const claimEndTime = this.claimEndTime;
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!claimEndTime || !eventTimezone) {
      return claimEndTime;
    }
    const shifted = this.DateTimeService.shiftDateObjectFromLocal(claimEndTime, eventTimezone);

    if (
      this._lastClaimEndTimeShifted
      && this._lastClaimEndTimeShifted.getTime() === shifted.getTime()
    ) {
      return this._lastClaimEndTimeShifted;
    }

    this._lastClaimEndTimeShifted = shifted;
    return shifted;
  }

  set claimEndTimeShifted(date) {
    const eventTimezone = this.position && this.position.event && this.position.event.timezoneName;
    if (!date) {
      this.claimEndTime = date;
      return;
    }

    this.claimEndTime = this.DateTimeService.unshiftDateObjectFromLocal(date, eventTimezone);
  }

  get claimEndTime() {
    return this._claimEndTime;
  }

  get claimDurationInHours() {
    let duration = '';
    if (isDateObject(this.claimStartTime) && isDateObject(this.claimEndTime)) {
      let [start, end] = normalizeSingleDayStartEndDates(this.claimStartTime, this.claimEndTime);
      duration = formattedDurationInHours(start, end);
    }
    return duration;
  }

  /**
   * Reference to parent controller
   * @param ctrl
   */
  set claimsModalController(ctrl) {
    this._claimsModalController = ctrl;
  }

  get claimsModalController() {
    return this._claimsModalController;
  }

  get showRouteSegment() {
    let show = false;
    if (
      this.editingClaim &&
      this.editingClaim.category &&
      this.editingClaim.category.constant !== 'CLAIM_CATEGORY_AMOUNT' &&
      this.editingClaim.type &&
      castToBoolean(this.editingClaim.type.collect_route_segments) &&
      this.position
      && (
        this.position.shiftSegment
        || this.position.shift_segments_id
      )
      && (
        !castToBoolean(this.event.opt_out_travel_standard)
        || !castToBoolean(this.event.opt_out_travel_tiered)
      )
    ) {
      show = true;
    }

    if (!show) {
      if (this.editingClaim && this.editingClaim.routeSegment) {
        this.editingClaim.routeSegment = null;
        this.editingClaim.route_segments_id = null;
      }
    }

    return show;
  }

  getRouteSegments() {
    let segments = [];
    if (this._staffUser && this._staffUser.route_segments) {
      segments = this._staffUser.route_segments;
    }

    if (this.editingClaim && this.editingClaim.category) {
      switch (this.editingClaim.category.constant) {
        case 'CLAIM_CATEGORY_DISTANCE':
          segments = segments.filter(
            segment =>
              segment.definition === 'ROUTE_SEGMENT_DISTANCE_BETWEEN'
              || segment.definition === 'ROUTE_SEGMENT_DISTANCE_HOME_TO_FIRST'
              || segment.definition === 'ROUTE_SEGMENT_DISTANCE_LAST_TO_HOME'
          );
          break;
        case 'CLAIM_CATEGORY_TIME':
          segments = segments.filter(
            segment =>
              segment.definition === 'ROUTE_SEGMENT_TIME_BETWEEN'
              || segment.definition === 'ROUTE_SEGMENT_TIME_HOME_TO_FIRST'
              || segment.definition === 'ROUTE_SEGMENT_TIME_LAST_TO_HOME'
          );
          break;
      }
    }

    if (this.position && (this.position.shiftSegment || this.position.shift_segments_id)) {
      let switch_on;
      if (this.position.shiftSegment) {
        switch_on = this.position.shiftSegment.definition;
      } else {
        switch_on = parseInt(this.position.shift_segments_id);
      }

      switch(switch_on) {
        case 1:
        case 'SHIFT_SEGMENT_FIRST':
          segments = segments.filter(
            segment =>
              segment.definition === 'ROUTE_SEGMENT_DISTANCE_HOME_TO_FIRST'
              || segment.definition === 'ROUTE_SEGMENT_TIME_HOME_TO_FIRST'
          );
          break;
        case 2:
        case 'SHIFT_SEGMENT_NOT_FIRST_OR_LAST':
          segments = segments.filter(
            segment =>
              segment.definition === 'ROUTE_SEGMENT_DISTANCE_BETWEEN'
              || segment.definition === 'ROUTE_SEGMENT_TIME_BETWEEN'
          );
          break;
        case 3:
        case 'SHIFT_SEGMENT_LAST':
          segments = segments.filter(
            segment =>
              segment.definition === 'ROUTE_SEGMENT_DISTANCE_BETWEEN'
              || segment.definition === 'ROUTE_SEGMENT_TIME_BETWEEN'
              || segment.definition === 'ROUTE_SEGMENT_DISTANCE_LAST_TO_HOME'
              || segment.definition === 'ROUTE_SEGMENT_TIME_LAST_TO_HOME'
          );
          break;
        case 4:
        case 'SHIFT_SEGMENT_ONLY':
          segments = segments.filter(
            segment =>
              segment.definition === 'ROUTE_SEGMENT_DISTANCE_LAST_TO_HOME'
              || segment.definition === 'ROUTE_SEGMENT_TIME_LAST_TO_HOME'
              || segment.definition === 'ROUTE_SEGMENT_DISTANCE_HOME_TO_FIRST'
              || segment.definition === 'ROUTE_SEGMENT_TIME_HOME_TO_FIRST'
          );
          break;
      }
    }


    let used_segment_ids = this.event.used_segment_ids;

    segments = segments.filter(segment => used_segment_ids.indexOf(parseInt(segment.id)) === -1);

    if (this.editingClaim && this.editingClaim.routeSegment && this.editingClaim.routeSegment.id) {
      let selected_segment = this.staffUser.route_segments.filter(segment => parseInt(segment.id) === parseInt(this.editingClaim.routeSegment.id));
      let the_selected_segment = selected_segment.pop();
      if (!isNaN(parseInt(the_selected_segment.id)) && segments.indexOf(the_selected_segment) === -1) {
        segments.push(the_selected_segment);
      }
    }

    return segments;
  }

  validate() {
    const form = this.$scope.secondaryClaimForm;
    this._clearErrors();

    let amount = 0;
    if (form.amount) {
      amount = parseFloat(form.amount.$modelValue);
    }

    if (form.$valid) {
      return true;
    } else {
      this.LogService.warn("form not valid! $error:", form.$error);
      for (const key in form.$error) {
        for (const idx in form.$error[key]) {
          const errorInfo = form.$error[key][idx];
          const name = errorInfo.$name.substring(0,1).toUpperCase() + errorInfo.$name.substring(1);
          let error;
          switch (key) {
            case 'required':
              error = name + ' is required';
              break;
            case 'min':
              error = name + ' value cannot be negative';
              break;
            default:
              error = 'Error with ' + name;
              break;
          }
          this._addError(error);
        }
      }
      this.LogService.warn("errors:", this.errors);
      return false;
    }
  }

  /**
   * Called when parent form gets saved
   * @returns {*}
   */
  autoAddOrSave(validate = true) {
    this.LogService.debug(`SecondaryClaimEditor autoAddOrSave(validate=${validate})`);
    if (this.isEmptyNewClaim) {
      // No edits; ignore
      return this.$q.when(null);
    } else if (this.isNewClaim) {
      // New claim; add
      return this.add(validate);
    } else {
      // Claim already exists
      if (this.isPristine && this.isSyncedToServer) {
        // Claim is synced, and no changes; don't need to save
        return this.$q.when(null);
      } else {
        // Local changes need to be sent to server
        return this.save(validate);
      }
    }
  }

  add(validate = true) {
    // Never add an empty claim
    if (this.isEmptyNewClaim) {
      return this.$q.reject(createError({
        error: 'SecondaryClaimFormEmpty',
        message: "Can't add an empty claim",
      }));
    }

    if (!validate || this.validate()) {
      this.LogService.debug(`SecondaryClaimEditor add(validate=${validate}) preparing to save/add`);

      this.prepareToSaveEditingClaim();
      let addedClaim = this.editingClaim;
      if (this.onAdd) {
        this.onAdd({claim: addedClaim});
      }
      return this.reset()
        .then(() => addedClaim);
    } else {
      this.LogService.debug(`SecondaryClaimEditor add(validate=${validate}) did not validate`);
      return this.$q.reject(createError({
        error: 'SecondaryClaimFormInvalid',
        message: "Form is invalid; cannot add secondary claim",
      }));
    }
  }

  save(validate = true) {
    if (!validate || this.validate()) {
      this.LogService.debug(`SecondaryClaimEditor save(validate=${validate}) preparing to save/add`);

      this.prepareToSaveEditingClaim();

      this.$q.when(this.copyClaim(this.editingClaim, this.claim))
        .then((promise) => {
          if (this.onSave) {
            this.onSave({claim: promise, replace: this.claim});
          }
          return promise;
        })
        .catch(err => {
          this.LogService.error("Error copying/saving claim:", err);
          return this.$q.reject(err);
        });
    } else {
      this.LogService.warn(`SecondaryClaimEditor save(validate=${validate}) form did not validate`);
      return this.$q.reject(createError({
        error: 'SecondaryClaimFormInvalid',
        message: "Form is invalid; cannot add secondary claim",
      }));
    }
  }

  remove() {
    this._removeAutoSaveHandler();
    if (this.onRemove) {
      this.onRemove({claim: this.claim});
    }
  }

  resetClaimStartEndTime() {
    if (this.editingClaim && this.editingClaim.claim_start_time) {
      this.claimStartTime = this.editingClaim.claim_start_time;
    } else if (this.position && this.position.start_time) {
      this.claimStartTime = this.position.start_time;
    } else {
      this.claimStartTime = new Date(defaultStartTime.getTime());
    }

    if (this.editingClaim && this.editingClaim.claim_end_time) {
      this.claimEndTime = this.editingClaim.claim_end_time;
    } else {
      this.claimEndTime = new Date(this.claimStartTime.getTime() + 3600000);
    }
  }

  writeStateToClaim() {
    this.editingClaim.claim_start_time = this.claimStartTime;
    this.editingClaim.claim_end_time = this.claimEndTime;
  }

  prepareToSaveEditingClaim() {
    this.writeStateToClaim(this.editingClaim);
  }

  reset() {
    let claimPromise;
    this.distanceLimitException             = false;
    this.distanceLimitExceptionAcknowledged = false;
    this.timeLimitException                 = false;
    this.timeLimitExceptionAcknowledged     = false;

    if (this.claim) {
      claimPromise = this.$q.when(this.copyClaim(this.claim));
    } else {
      claimPromise = this.ClaimService.createSecondaryClaimForPosition(this.staffUser, this.position);

    }

    this.editingClaim = {};
    return claimPromise
      .then(claim => {
        claim.inExceptionState = false;
        this.editingClaim = claim;
        if (this.position) {
          this.position.editingSecondaryClaim = claim;
        }
        this.resetClaimStartEndTime();
        let form = this.$scope.secondaryClaimForm;
        if (form) {
          form.$setPristine();
          form.$setUntouched();
        }
      })
      .catch(err => {
        this.LogService.error("Error resetting claim:", err);
        return this.$q.reject(err);
      });
  }

  receiptFileChanged($files, $file, $newFiles, $duplicateFiles, $invalidFiles, $event) {
    this.LogService.log("receiptFileChanged. arguments:", arguments, "this:", this);

    // Create document from file
    return this.DocumentService.createWithLocalFile($file)
      .then(document => {
        this.LogService.log("Set editingClaim.receiptDocument to:", document);
        this.editingClaim.receiptDocument = document;

        // Remove file upload
        this.LogService.log("Removing receiptFileForUpload from editingClaim");
        this.editingClaim.receiptFileForUpload = null;
      })
      .catch(err => {
        this.LogService.error("Error creating document:", err);
        return this.$q.reject(err);
      });
  }

  copyClaim(src, dst) {
    return this.ClaimService.copy(src, dst);
  }

  updateTimeBasedClaim() {
    // Limit in admin is in minutes but calculated duration here is in hours
    let limit = this.getExceptionByName('time_based_units_limit');
    this.timeLimitException = !!(limit && parseFloat(limit.value) > 0 && parseFloat(limit.value) < (this.claimDurationInHours * 60) && !this.timeLimitExceptionAcknowledged);
  }

  claimDistanceChange() {
    let limit = this.getExceptionByName('distance_based_units_limit');
    this.distanceLimitException = !!(limit && parseFloat(limit.value) > 0 && parseFloat(limit.value) < parseFloat(this.editingClaim.units) && !this.distanceLimitExceptionAcknowledged);
  }


  getExceptionByName(name) {
    let except = null;
    if (this.staffUser && this.staffUser.payroll) {
      except = this.staffUser.payroll.find(el => el.name === name);
    }
    return except;
  }

  claimIsInExceptionState() {
    if (this.editingClaim && this.editingClaim.category) {
      if (this.editingClaim.category.constant === 'CLAIM_CATEGORY_TIME') {
        let timeException = (this.timeLimitException === true && this.timeLimitExceptionAcknowledged === false);
        this.editingClaim.inExceptionState = timeException;
        return timeException;
      }
      if (this.editingClaim.category.constant === 'CLAIM_CATEGORY_DISTANCE') {
        let distanceException = (this.distanceLimitException === true && this.distanceLimitExceptionAcknowledged === false);
        this.editingClaim.inExceptionState = distanceException;
        return distanceException;
      }
    }
    return false;
  }


  /****************************
   * Date picker modals
   ****************************/

  get datePickersOpened() {
    return this._datePickersOpened;
  }

  get datePickersOptions() {
    return this._datePickersOptions;
  }

  openDatePicker(datePickerName) {
    if (this.event) {
      if (this.event.recap_available_events_start_time && this.event.recap_available_events_end_time) {
        this._datePickersOptions = {
          minDate: Math.max.apply(null, [new Date(this.event.programs_start_date), new Date(this.event.recap_available_events_start_time)]),
          maxDate: Math.min.apply(null, [new Date(this.event.programs_end_date), new Date(this.event.recap_available_events_end_time)]),
          showWeeks: false,
        };
      } else {
        if (this.event.programs_start_date && this.event.programs_end_date) {
          this._datePickersOptions = {
            minDate: Math.max.apply(null, [new Date(this.event.programs_start_date)]),
            maxDate: Math.min.apply(null, [new Date(this.event.programs_end_date)]),
            showWeeks: false,
          };
        } else {
          this._datePickersOptions = {
            showWeeks: false,
          };
        }
      }
    } else {
      return; // No program or event type chosen, no date can be chosen
    }

    this._datePickersOpened[datePickerName] = true;
  }

  isDatePickerOpen(datePickerName) {
    return !!this.datePickersOpened[datePickerName];
  }


  /**
   * Private methods
   */

  _addError(err) {
    this.errors.push(err);
  }

  _clearErrors() {
    this.errors = [];
  }

  _addAutoSaveHandler() {
    if (!this._autoSaveHandler) {
      this._autoSaveHandler = (validate = true) => this.autoAddOrSave(validate);
      this.claimsModalController.addAutoSaveSecondaryClaimHandler(this._autoSaveHandler);
    }
  }

  _removeAutoSaveHandler() {
    if (this._autoSaveHandler) {
      this.claimsModalController.removeAutoSaveSecondaryClaimHandler(this._autoSaveHandler);
      this._autoSaveHandler = null;
    }
  }
}

export default SecondaryClaimEditorController;
